// ./src/utils/api/CauseService.ts
//
// ROADMAP #204 — causes: what people give to. An ACCOUNT catalog row, pinned to one organizer page or to none (= every
// page), with a currency, a goal, a giving window, suggested amounts, a fee mode and a receipt kind. The API's
// CauseCore validates every rule (official receipts need complete receipt details; the currency is fixed once a gift
// exists) and reads back each cause's goal number. OpResult-native; `total` on the list result.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// Mirrors eventsmagic-api boundary/donation.ts + boundary/event.ts FEE_MODE.
export const RECEIPT_KIND_VALUES = ["none", "plain", "official"] as const;
export const SHOW_DONORS_VALUES = ["none", "names", "names_and_amounts"] as const;
export const CAUSE_FEE_MODE_VALUES = ["absorb", "service", "service_processor"] as const;

export type ReceiptKind = (typeof RECEIPT_KIND_VALUES)[number];
export type ShowDonors = (typeof SHOW_DONORS_VALUES)[number];

const CAUSE_FIELDS = `
  id
  accountId
  organizerProfileId
  name
  summary
  description
  imageUploadedFileId
  imageUploadedFile { id fileUrl thumbnailUrl }
  currencyCode
  goalAmount
  startsAt
  endsAt
  suggestedAmounts
  minAmount
  advantageAmount
  advantageDescription
  allowCustomAmount
  feeMode
  receiptKind
  showDonors
  orderNo
  raisedAmount
  donationsCount
  receiptMissing
  status
  rights
  createdAt
  updatedAt
`;

const ERRORS = `errors { name errors warnings }`;

const CAUSE_LIST_QUERY = gql`
  query CauseList($filter: CauseFilter, $params: PaginationAndSorting) {
    causeList(filter: $filter, params: $params) {
      code
      total
      data { ${CAUSE_FIELDS} }
      ${ERRORS}
    }
  }
`;

const CAUSE_GET_QUERY = gql`
  query CauseGet($id: ID) {
    causeGet(id: $id) {
      code
      data { ${CAUSE_FIELDS} }
      ${ERRORS}
    }
  }
`;

const CAUSE_CREATE_MUTATION = gql`
  mutation CauseCreate($params: CauseInput) {
    causeCreate(params: $params) {
      code
      data { ${CAUSE_FIELDS} }
      ${ERRORS}
    }
  }
`;

const CAUSE_UPDATE_MUTATION = gql`
  mutation CauseUpdate($where: CauseWhereInput, $params: CauseInput) {
    causeUpdate(where: $where, params: $params) {
      code
      data { ${CAUSE_FIELDS} }
      ${ERRORS}
    }
  }
`;

const CAUSE_REMOVE_MUTATION = gql`
  mutation CauseRemove($where: CauseWhereInput) {
    causeRemove(where: $where) {
      code
      data { id }
      ${ERRORS}
    }
  }
`;

export interface Cause {
  id?: string;
  accountId?: string;
  /** The one organizer page that shows it; null = every page of the account. */
  organizerProfileId?: string | null;
  name?: string;
  summary?: string | null;
  description?: string | null;
  imageUploadedFileId?: string | null;
  /** The picture, resolved by ms_storage. */
  imageUploadedFile?: { id?: string; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  currencyCode?: string;
  /** Null = no goal shown. */
  goalAmount?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  suggestedAmounts?: number[] | null;
  minAmount?: number | null;
  /** #204 — the fair value of what each gift receives in return (null / 0 = nothing), and what it is. */
  advantageAmount?: number | null;
  advantageDescription?: string | null;
  allowCustomAmount?: boolean;
  feeMode?: string;
  receiptKind?: ReceiptKind | string;
  showDonors?: ShowDonors | string;
  orderNo?: number | null;
  /** Received, non-test gifts so far (server aggregate). */
  raisedAmount?: number;
  donationsCount?: number;
  /** Official receipts: the receipt details' fields still missing ([] = ready). */
  receiptMissing?: string[];
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CauseInput {
  organizerProfileId?: string | null;
  name?: string;
  summary?: string | null;
  description?: string | null;
  imageUploadedFileId?: string | null;
  currencyCode?: string;
  goalAmount?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  suggestedAmounts?: number[] | null;
  minAmount?: number | null;
  advantageAmount?: number | null;
  advantageDescription?: string | null;
  allowCustomAmount?: boolean;
  feeMode?: string;
  receiptKind?: string;
  showDonors?: string;
  orderNo?: number | null;
  status?: number;
}

export interface CauseFilter {
  id?: string[] | null;
  organizerProfileId?: string[] | null;
  receiptKind?: string[] | null;
  searchKeyword?: string | null;
}

/** Causes — the account's catalog of things to give to. */
export class CauseService {
  static async list(filter?: CauseFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(CAUSE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(CAUSE_GET_QUERY, { id });
  }

  static async create(params: CauseInput): Promise<OpResult> {
    return ApiService.executeMutation(CAUSE_CREATE_MUTATION, { params });
  }

  static async update(id: string, params: CauseInput): Promise<OpResult> {
    return ApiService.executeMutation(CAUSE_UPDATE_MUTATION, { where: { id: [id] }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(CAUSE_REMOVE_MUTATION, { where: { id: [id] } });
  }
}
